import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Button, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'

const isDev = import.meta.env.DEV

/**
 * Themed fallback rendered by an in-layout error boundary. Sits inside
 * `ThemeProvider`, so it can use MUI. Fills its container (not the viewport)
 * so the surrounding nav/sidebar stays visible while only the failed region
 * shows the error.
 *
 * Receives `react-error-boundary` props: { error, resetErrorBoundary }.
 */
const ErrorFallback = ({
    error,
    resetErrorBoundary,
    title = 'Something went wrong',
    message = 'This section failed to load. You can try again, or carry on using the rest of the page.'
}) => (
    <Box
        role="alert"
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 2,
            p: 4,
            minHeight: 240,
            width: '100%'
        }}
    >
        <WarningAmberIcon color="warning" sx={{ fontSize: 48 }} />
        <Typography variant="h6" color="textPrimary">
            {title}
        </Typography>
        <Typography
            variant="body2"
            color="textSecondary"
            sx={{ maxWidth: 480 }}
        >
            {message}
        </Typography>

        {isDev && error?.message && (
            <Box
                component="pre"
                sx={{
                    mt: 1,
                    p: 2,
                    maxWidth: '100%',
                    overflow: 'auto',
                    textAlign: 'left',
                    fontSize: 12,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    color: 'error.main'
                }}
            >
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ''}
            </Box>
        )}

        {resetErrorBoundary && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={resetErrorBoundary}
                >
                    Try again
                </Button>
            </Stack>
        )}
    </Box>
)

ErrorFallback.propTypes = {
    error: PropTypes.object,
    resetErrorBoundary: PropTypes.func,
    title: PropTypes.string,
    message: PropTypes.string
}

export default ErrorFallback
